import { NavigateOptions } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

import { useCurrentLocale } from './locale';

// can be used only in app router folders
// will crash if used in pages router

export type UniversalRouter = {
  locale?: string;
  asPath: string;
  pathname: string;
  isReady: boolean;
  query: Record<string, string>;
  reload: () => void;
  push: (url: string) => Promise<boolean>;
  replace: (url: string) => Promise<boolean>;
} & ReturnType<typeof useRouter>;

// if router null, we are in app router, isReady always true
// otherwise we do as before
export const useIsRouterReadyLegacy = () => {
  const [isReady, setIsReady] = useState(false);
  const router = useRouter();

  useEffect(() => {
    setIsReady(true);
  }, []);

  return router && isReady;
};

export const useUniversalRouter = (): UniversalRouter => {
  const appRouter = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const queryStr = searchParams.toString() ? `?${searchParams.toString()}` : '';
  const { locale } = useCurrentLocale();

  const router = useMemo(
    () => ({
      locale,
      asPath: `${pathname}${queryStr}`,
      pathname,
      isReady: true,
      query: Object.fromEntries(searchParams || new Map()),
      reload: appRouter.refresh,
      ...appRouter,
      push: (href: string, options?: NavigateOptions) => {
        const path =
          href?.startsWith('/') || href?.includes('http') ? href : `/${href}`;
        appRouter.push(
          path?.startsWith(`/${locale}`) || path?.includes('http')
            ? href
            : `/${locale}${href}`,
          options
        );

        return Promise.resolve(true);
      },
      replace: (href: string, options?: NavigateOptions) => {
        const path =
          href?.startsWith('/') || href?.includes('http') ? href : `/${href}`;
        appRouter.replace(
          path?.startsWith(`/${locale}`) || path?.includes('http')
            ? href
            : `/${locale}${href}`,
          options
        );

        return Promise.resolve(true);
      },
    }),
    [appRouter, pathname, queryStr, searchParams, locale]
  );

  return router;
};
